import ServiceSelector from '@/components/service-selector/ServiceSelector';
import {
  IServiceBlock,
  IServiceBlockConfig,
  getServiceBlockTiers,
  useBlocksActions,
} from '@/models/commercial-proposals/blocks.store';
import { useProposal } from '@/models/commercial-proposals/proposal.store';
import { ActionIcon, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { CgRowFirst, CgRowLast } from 'react-icons/cg';
import { HiCursorClick } from 'react-icons/hi';
import { PiPlusFill } from 'react-icons/pi';
import { IPriceCellConfig } from './cells/ServicePriceCell/ServicePriceCellSettings';

export const SSelector = ({
  children,
  block,
}: {
  children: (props: { onClick: () => void }) => React.ReactNode;
  block: IServiceBlock;
}) => {
  const proposal = useProposal();
  const [opened, { open, close }] = useDisclosure(false);
  const { update } = useBlocksActions();

  if (!proposal) return null;

  return (
    <>
      {children({ onClick: open })}
      <ServiceSelector
        currency={proposal!.currency}
        jurisdiction={proposal!.jurisdiction}
        opened={opened}
        onClose={close}
        exclude={block.services
          .filter((service) => !service.parentId)
          .map((service) => service.serviceId)}
        onSelect={(service) => {
          update({
            ...block,
            services: [
              ...block.services,
              {
                blockUuid: block.uuid,
                serviceId: service.id,
                price: {
                  amount: service.price,
                  currency: service.currency,
                },
                config: {
                  showDescription: false,
                  showChildServices: false,
                  customDescription: service.description || '',
                  ...Array.from({
                    length: getServiceBlockTiers(block.type),
                  }).reduce(
                    (acc: Record<number, IPriceCellConfig>, _, index) => {
                      acc[index] = {
                        included: true,
                        displayIcon: true,
                      };
                      return acc;
                    },
                    {},
                  )!,
                },
                weight: block.services.length,
              } as IServiceBlock['services'][number],
            ],
          });
        }}
      />
    </>
  );
};

export default function ServiceBlockControls({
  block,
}: {
  block: IServiceBlock;
}) {
  const { update } = useBlocksActions();

  const color = (option: boolean) =>
    option ? 'var(--mantine-color-blue-8)' : 'var(--mantine-color-gray-5)';

  const toggle = (option: keyof IServiceBlockConfig) =>
    update({
      ...block,
      config: { ...block.config, [option]: !block.config[option] },
    });

  return (
    <>
      <Tooltip label="Make non-interactive">
        <ActionIcon
          variant="subtle"
          color={color(block.config.nonInteractive)}
          onClick={() => toggle('nonInteractive')}
        >
          <HiCursorClick size={16} />
        </ActionIcon>
      </Tooltip>

      <SSelector block={block}>
        {({ onClick }) => (
          <Tooltip label="Add Service">
            <ActionIcon variant="subtle" color="gray" onClick={onClick}>
              <PiPlusFill size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </SSelector>

      <Tooltip label="Show Heading Row">
        <ActionIcon
          variant="subtle"
          color={color(block.config.showHeadingRow)}
          onClick={() => toggle('showHeadingRow')}
        >
          <CgRowFirst size={16} />
        </ActionIcon>
      </Tooltip>

      <Tooltip label="Show Summary Row">
        <ActionIcon
          variant="subtle"
          color={color(block.config.showSummaryRow)}
          onClick={() => toggle('showSummaryRow')}
        >
          <CgRowLast size={16} />
        </ActionIcon>
      </Tooltip>
    </>
  );
}
